import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { useLocation as useZyroLocation } from "../context/LocationContext";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";

const TYPES = ["All", "Single Room", "1BHK", "2BHK", "3BHK", "Studio", "PG"];
const FURN = ["Any", "Unfurnished", "Semi-Furnished", "Fully Furnished"];

export default function RentalsPage() {
  const [params] = useSearchParams();
  const { city } = useZyroLocation();
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState("All");
  const [furn, setFurn] = useState("Any");
  const [rent, setRent] = useState([5000, 80000]);
  const [cityFilter, setCityFilter] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = { min_rent: rent[0], max_rent: rent[1] };
    if (cityFilter) p.city = city;
    if (q) p.q = q;
    if (type !== "All") p.room_type = type;
    if (furn !== "Any") p.furnishing = furn;
    api.get("/properties", { params: p })
      .then(({ data }) => setRooms(data))
      .finally(() => setLoading(false));
  }, [city, cityFilter, q, type, furn, rent]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
        {cityFilter ? `Rentals in ${city}` : "All rentals"}
      </p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-8">
        {rooms.length} home{rooms.length === 1 ? "" : "s"} for long stays
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
        <aside className="lg:col-span-1" data-testid="rentals-filter">
          <div className="sticky top-28 bg-white border border-black/5 rounded-2xl p-6 space-y-6">
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Locality / title" className="pl-9" data-testid="rentals-search" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">Room type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">Furnishing</Label>
              <Select value={furn} onValueChange={setFurn}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FURN.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">Monthly rent</Label>
              <Slider value={rent} onValueChange={setRent} min={2000} max={100000} step={1000} />
              <div className="flex justify-between text-xs text-midnight mt-2">
                <span>₹{rent[0].toLocaleString("en-IN")}</span>
                <span>₹{rent[1].toLocaleString("en-IN")}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Only in {city}</Label>
              <Switch checked={cityFilter} onCheckedChange={setCityFilter} />
            </div>
          </div>
        </aside>

        <div className="lg:col-span-3">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/3] bg-stone rounded-2xl animate-pulse" />)}
            </div>
          ) : rooms.length === 0 ? (
            <div className="text-center py-24" data-testid="no-rentals">
              <p className="font-serif text-2xl text-midnight mb-2">No rentals match your filters</p>
              <p className="text-neutral-500">Try widening the price range or turning off city filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger">
              {rooms.map((r) => <PropertyCard key={r.id} property={r} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
